// Privacy & Terms boilerplate pages — generated 2026-02. Conservative
// defaults for a one-person indie SaaS that uses Google sign-in,
// Stripe for payments, and stores progress in MongoDB. Edit the
// CONTACT_EMAIL and BUSINESS_NAME constants below if your contact
// address or trading name changes.

const CONTACT_EMAIL = "support@kanjibuild.com";
const BUSINESS_NAME = "Kanji Build";
const SERVICE_URL = "https://kanjibuild.com";
const EFFECTIVE = "26 February 2026";

import { Link } from "react-router-dom";

function LegalShell({ title, children, testid }) {
  return (
    <div data-testid={testid} className="min-h-screen bg-washi text-sumi">
      <header className="border-b border-borderDefault px-6 md:px-10 py-4 flex items-center justify-between">
        <Link to="/" className="font-serif text-2xl text-shu">寺</Link>
        <Link
          to="/welcome"
          className="font-sans text-[11px] uppercase tracking-[0.25em] text-sumiSoft hover:text-sumi transition-colors"
        >
          ← back to kanji build
        </Link>
      </header>
      <article className="max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16 font-serif text-base leading-relaxed">
        <h1 className="font-serif text-4xl md:text-5xl text-sumi mb-2">{title}</h1>
        <p className="font-sans text-xs uppercase tracking-[0.25em] text-sumiSoft mb-12">
          effective {EFFECTIVE}
        </p>
        {children}
        <footer className="mt-16 pt-8 border-t border-borderDefault font-sans text-xs text-sumiSoft">
          Questions? Email <a className="text-shu hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </footer>
      </article>
    </div>
  );
}

export function PrivacyPolicy() {
  return (
    <LegalShell title="Privacy Policy" testid="page-privacy">
      <p>
        {BUSINESS_NAME} ("we", "us") provides {SERVICE_URL} (the
        "Service"). This policy explains what data we collect, why,
        and what you can do about it.
      </p>

      <h2 className="font-serif text-2xl text-sumi mt-10 mb-3">What we collect</h2>
      <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
        <li>
          <b>Account information.</b> When you sign in with Google, we
          receive your email address, your name, and your Google
          profile picture URL. That&apos;s it — we do not request access
          to Gmail, Drive, Calendar, or any other Google data.
        </li>
        <li>
          <b>Learning progress.</b> Each kanji you mark as &quot;known&quot; /
          &quot;studying&quot; / &quot;skip&quot;, every word you&apos;ve reviewed, and any
          personal mnemonics you type into the app are stored in our
          database, keyed by your account. This is the data the
          app is built to keep — it&apos;s the point.
        </li>
        <li>
          <b>Payment information.</b> If you upgrade to the lifetime
          plan, the £20 transaction is handled entirely by Stripe.
          Stripe stores your card details under its PCI-DSS-compliant
          systems; we receive only a confirmation that you paid, the
          last four digits of your card, and the country your card
          was issued in.
        </li>
        <li>
          <b>Technical logs.</b> Our server logs each request URL,
          the timestamp, and your IP address for security and
          debugging. Logs are retained for 30 days then rotated away.
        </li>
      </ul>
      <p>
        We do <em>not</em> use cookies for analytics, do not run
        third-party ad pixels, and do not share, sell, or rent your
        data to anyone.
      </p>

      <h2 className="font-serif text-2xl text-sumi mt-10 mb-3">Why we collect it</h2>
      <p>Strictly the minimum to operate the Service:</p>
      <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
        <li>Your email + name → so we can identify your account across devices and address you in the app.</li>
        <li>Your progress data → so your learning persists between sessions and devices.</li>
        <li>Your payment confirmation → so we know to unlock the paid features.</li>
      </ul>

      <h2 className="font-serif text-2xl text-sumi mt-10 mb-3">Sub-processors</h2>
      <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
        <li><b>Google</b> (authentication only). <a className="text-shu hover:underline" href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">Google Privacy Policy</a></li>
        <li><b>Stripe</b> (payment processing). <a className="text-shu hover:underline" href="https://stripe.com/privacy" target="_blank" rel="noreferrer">Stripe Privacy Policy</a></li>
        <li><b>Emergent</b> (the platform that hosts our servers and database). <a className="text-shu hover:underline" href="https://emergent.sh" target="_blank" rel="noreferrer">emergent.sh</a></li>
      </ul>

      <h2 className="font-serif text-2xl text-sumi mt-10 mb-3">Your rights (GDPR / UK GDPR)</h2>
      <p>
        You can ask us at any time to:
      </p>
      <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
        <li>Send you a copy of all data we hold about you (data portability).</li>
        <li>Correct any inaccurate data.</li>
        <li>Permanently delete your account and all associated data (right to erasure).</li>
        <li>Withdraw consent for processing — in practice, this means closing your account.</li>
      </ul>
      <p>
        Email <a className="text-shu hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> with the subject &quot;Data request&quot; and we&apos;ll
        respond within 30 days. We do not require any specific format
        or documentation — your authenticated account is your proof
        of identity.
      </p>

      <h2 className="font-serif text-2xl text-sumi mt-10 mb-3">Changes</h2>
      <p>
        If we materially change this policy we will email every account
        holder at least 14 days before the change takes effect, with a
        plain-language summary of what changed.
      </p>
    </LegalShell>
  );
}

export function TermsOfService() {
  return (
    <LegalShell title="Terms of Service" testid="page-terms">
      <p>
        These terms cover your use of {SERVICE_URL} (the "Service")
        provided by {BUSINESS_NAME} ("we", "us"). By signing in or
        making a purchase you accept these terms.
      </p>

      <h2 className="font-serif text-2xl text-sumi mt-10 mb-3">The service</h2>
      <p>
        {BUSINESS_NAME} is an educational web app that helps you
        learn Japanese kanji. We do our best to keep it available, but
        we make no guarantee of uptime — outages may happen for
        maintenance, hosting issues, or simply because we're a small
        operation. Your learning progress is backed up but not under a
        formal SLA.
      </p>

      <h2 className="font-serif text-2xl text-sumi mt-10 mb-3">Account &amp; eligibility</h2>
      <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
        <li>You must have a valid Google account to sign in.</li>
        <li>You must be at least 13 years old, or have a parent/guardian&apos;s permission if younger.</li>
        <li>You are responsible for keeping access to your Google account secure.</li>
        <li>You may close your account at any time by emailing us — see the Privacy Policy for data deletion.</li>
      </ul>

      <h2 className="font-serif text-2xl text-sumi mt-10 mb-3">Lifetime purchase &amp; refunds</h2>
      <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
        <li>
          The "Lifetime" plan is a one-off payment of £20 GBP that
          unlocks the full app for the lifetime of the Service.
          "Lifetime" means as long as we operate {SERVICE_URL}.
        </li>
        <li>
          <b>Refund policy:</b> if you are unhappy with your purchase
          for any reason, email <a className="text-shu hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> within
          14 days for a no-questions-asked full refund. This is in
          addition to your statutory consumer rights under UK / EU law.
        </li>
        <li>
          Payments are processed by Stripe under their own terms — see
          <a className="text-shu hover:underline" href="https://stripe.com/legal/consumer" target="_blank" rel="noreferrer"> Stripe&apos;s consumer terms</a>.
        </li>
      </ul>

      <h2 className="font-serif text-2xl text-sumi mt-10 mb-3">Acceptable use</h2>
      <p>You agree NOT to:</p>
      <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
        <li>Use the Service for any illegal purpose.</li>
        <li>Attempt to break into other users&apos; accounts or our systems.</li>
        <li>Scrape, copy, or re-distribute our kanji dataset, illustrations, mnemonics, or any other content beyond personal study use.</li>
        <li>Resell, sublicense, or build a competing product on top of our data or features.</li>
      </ul>
      <p>
        Mnemonics and notes <em>you</em> create in the app remain
        yours; we only display them back to you. The kanji decomposition
        data, phonetic series, etymological visuals, and other learning
        structure are licensed to you for personal study — we retain
        copyright over the editorial structure.
      </p>

      <h2 className="font-serif text-2xl text-sumi mt-10 mb-3">No warranty</h2>
      <p>
        The Service is provided &quot;as is&quot; without warranties of any
        kind, express or implied. Educational content is provided in
        good faith but may contain errors — we do not warrant fitness
        for any particular purpose (e.g. JLPT exam preparation
        guarantee, business correspondence accuracy, etc.).
      </p>

      <h2 className="font-serif text-2xl text-sumi mt-10 mb-3">Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, our total liability
        in connection with the Service is limited to the amount you
        paid us in the 12 months before the event giving rise to the
        claim. Nothing in these terms limits liability for fraud,
        death, or personal injury caused by negligence.
      </p>

      <h2 className="font-serif text-2xl text-sumi mt-10 mb-3">Governing law</h2>
      <p>
        These terms are governed by the laws of England and Wales,
        and disputes are subject to the exclusive jurisdiction of the
        courts of England and Wales.
      </p>

      <h2 className="font-serif text-2xl text-sumi mt-10 mb-3">Changes</h2>
      <p>
        We may update these terms occasionally. Material changes will
        be announced by email at least 14 days in advance. Continued use
        after the effective date constitutes acceptance.
      </p>

      <h2 className="font-serif text-2xl text-sumi mt-10 mb-3">Contact</h2>
      <p>
        Email <a className="text-shu hover:underline" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> for anything else.
      </p>
    </LegalShell>
  );
}
